// src/services/LabTestApi/LabList.js
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || 'https://admin.medrphalabs.com/api/';
const IMAGE_BASE_URL = 'https://admin.medrphalabs.com/LabImage/';

/* ================= Calculate Distance (Haversine formula in KM) ================= */
export const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  const R = 6371; // Earth radius in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Number((R * c).toFixed(1));
};

/* ================= Fetch Nearby Labs (with lat/lng) ================= */
export const fetchNearbyLabsData = async (latitude, longitude) => {
  try {
    const res = await axios.get(`${BASE_URL}Lab/nearbyStores`, {
      params: {
        latitude: Number(latitude),
        longitude: Number(longitude),
      },
    });

    let labs = [];
    if (Array.isArray(res.data)) labs = res.data;
    else if (Array.isArray(res.data?.data)) labs = res.data.data;

    if (labs.length > 0) {
      return labs.map((lab) => {
        const imgName = (lab.lab_image || lab.labImage || "").trim();
        const imageUrl = imgName ? `${IMAGE_BASE_URL}${imgName}` : null;
        return {
          id: lab.id,
          labName: lab.lab_name || lab.labName || "Diagnostic Lab",
          name: lab.lab_name || lab.labName || "Diagnostic Lab",
          companyName: lab.company_name || lab.companyName || "Diagnostic Partner",
          description: lab.description || "",
          address: lab.address || "Certified Collection Center",
          latitude: Number(lab.latitude),
          longitude: Number(lab.longnitude || lab.longitude),
          distanceKm: lab.distanceKm || calculateDistanceKm(latitude, longitude, Number(lab.latitude), Number(lab.longnitude)),
          image: imageUrl,
          labImage: imageUrl,
          isActive: lab.isActive ?? true,
        };
      });
    }

    // Fallback to fetch all labs if nearby empty
    return await fetchLabsData(latitude, longitude);
  } catch (error) {
    console.error("Error in fetchNearbyLabsData, falling back to all labs:", error);
    return await fetchLabsData(latitude, longitude);
  }
};

/* ================= Fetch All Labs (Matches Flutter LabService.fetchAllLabs) ================= */
export const fetchLabsData = async (userLat = null, userLng = null) => {
  try {
    const res = await axios.get(`${BASE_URL}Lab`);
    let labs = [];
    if (Array.isArray(res.data)) labs = res.data;
    else if (Array.isArray(res.data?.data)) labs = res.data.data;

    // Matches Flutter App (_processLabs: lab.isLab != false)
    const mapped = labs
      .filter((lab) => lab.isLab !== false)
      .map((lab) => {
        const imgName = (lab.lab_image || lab.labImage || "").trim();
        const imageUrl = imgName ? `${IMAGE_BASE_URL}${imgName}` : null;
        
        let dist = null;
        if (userLat && userLng && lab.latitude && (lab.longnitude || lab.longitude)) {
          dist = calculateDistanceKm(userLat, userLng, Number(lab.latitude), Number(lab.longnitude || lab.longitude));
        }

        return {
          id: lab.id,
          labName: lab.lab_name || lab.labName || "Diagnostic Lab",
          name: lab.lab_name || lab.labName || "Diagnostic Lab",
          companyName: lab.company_name || lab.companyName || "Diagnostic Partner",
          description: lab.description || "",
          address: lab.address || "Certified Diagnostic Center",
          latitude: Number(lab.latitude),
          longitude: Number(lab.longnitude || lab.longitude),
          distanceKm: dist != null ? dist : (lab.distanceKm || 2.5),
          image: imageUrl,
          labImage: imageUrl,
          isActive: lab.isActive ?? true,
        };
      });

    // When location coordinates are active, sort and return labs strictly within 20km
    if (userLat && userLng) {
      const nearLabs = mapped.filter((lab) => {
        if (isNaN(lab.latitude) || isNaN(lab.longitude) || !lab.latitude || !lab.longitude) return false;
        const d = calculateDistanceKm(userLat, userLng, lab.latitude, lab.longitude);
        return d !== null && d <= 20; // Strictly 20km radius
      });
      return nearLabs.sort((a, b) => (Number(a.distanceKm) || 0) - (Number(b.distanceKm) || 0));
    }

    return mapped.sort((a, b) => (Number(a.distanceKm) || 0) - (Number(b.distanceKm) || 0));
  } catch (error) {
    console.error("Error fetching all labs:", error);
    return [];
  }
};
